import {ConsentPurpose, ConsentPurposeVersion, UserConsent} from '@/types'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {Box, Card, CardContent, Chip, Grid, IconButton, Stack, Typography} from '@mui/material'
import * as React from 'react'
import {useEffect} from 'react'
import {
	Datagrid,
	DateField,
	Filter,
	FunctionField,
	Pagination,
	ReferenceManyField,
	RichTextField,
	SearchInput,
	Show,
	Tab,
	TabbedShowLayout,
	TextField,
	useNotify,
	useShowController
} from 'react-admin'

const ConsentFilters = (props) => (
	<Filter {...props}>
		<SearchInput
			placeholder="Rechercher par token, email, nom..."
			alwaysOn
			source="q"
		/>
	</Filter>
)

export default function ConsentPurposeShow() {
	const {record, isLoading} = useShowController<ConsentPurpose>()
	const notify = useNotify()

	useEffect(() => {
		if (record) {
			document.title = `Usage RGPD - ${record.title}`
		}
	}, [record])

	const handleCopyHash = async (hash: string) => {
		try {
			await navigator.clipboard.writeText(hash)
			notify('Hash copié dans le presse-papiers', {type: 'success'})
		} catch (error) {
			notify('Erreur lors de la copie du hash', {type: 'error'})
		}
	}

	return (
		<Show>
			<TabbedShowLayout>
				<Tab label="Informations générales">
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Stack direction="row" spacing={3}>
								<FunctionField
									source="isActive"
									label="Statut"
									render={(record: ConsentPurpose) => (
										<Chip
											label={record.isActive ? 'Actif' : 'Inactif'}
											color={record.isActive ? 'success' : 'default'}
										/>
									)}
								/>
								{record && record.versionType &&
                                    <FunctionField
                                        source="versionType"
                                        label="Type de version"
                                        render={(record: ConsentPurpose) => (
											<Chip
												label={record.versionType === 'MAJOR' ? 'Majeure' : 'Mineure'}
												color={record.versionType === 'MAJOR' ? 'primary' : 'default'}
											/>
										)}
                                    />
								}
							</Stack>
						</Grid>
						<Grid item xs={12} md={6}>
							<Typography variant="h6" gutterBottom>
								Identité de l'usage
							</Typography>
							<Stack direction="row" spacing={3}>
								<TextField source="code" label="Code"/>
								<TextField source="title" label="Titre"/>
								<TextField source="version" label="Numéro de version"/>
							</Stack>
						</Grid>

						<Grid item xs={12} md={6}>
							<Typography variant="h6" gutterBottom>
								Métadonnées
							</Typography>
							<DateField source="createdAt" label="Créé le"/>
							<DateField source="updatedAt" label="Modifié le"/>

							{record?.userConsentCount !== undefined && (
								<Box sx={{mt: 2}}>
									<Typography variant="body2" color="textSecondary">
										Nombre d'utilisateurs ayant consenti
									</Typography>
									<Typography variant="h4">
										{record.userConsentCount}
									</Typography>
								</Box>
							)}
						</Grid>
					</Grid>

					<Box sx={{mt: 3}}>
						<Typography variant="h6" gutterBottom>
							Description
						</Typography>
						<RichTextField source="description"/>
					</Box>
				</Tab>

				<Tab label="Versions">
					<Typography variant="h6" gutterBottom>
						Historique des versions ({record?.versions?.length || 0} versions)
					</Typography>

					{record?.versions && record.versions.length > 0 ? (
						<Grid container spacing={2}>
							{record.versions.map((version: ConsentPurposeVersion) => (
								<Grid item xs={12} key={version.id}>
									<Card variant={version.isActive ? 'elevation' : 'outlined'} sx={{
										mb: 1,
										borderColor: version.isActive ? 'primary.main' : 'divider',
										borderWidth: version.isActive ? 2 : 1
									}}>
										<CardContent>
											<Box sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'start',
												mb: 1
											}}>
												<Typography variant="h6" component="div">
													Version {version.version}
													{version.isActive && (
														<Chip
															label="ACTIVE"
															color="success"
															size="small"
															sx={{ml: 1}}
														/>
													)}
												</Typography>
												{version &&
                                                    <Chip
                                                        label={version.versionType === 'MAJOR' ? 'Majeure' : 'Mineure'}
                                                        color={version.versionType === 'MAJOR' ? 'primary' : 'default'}
                                                        size="small"
                                                    />
												}
											</Box>

											<Typography variant="body1" fontWeight="medium" gutterBottom>
												{version.title}
											</Typography>

											<Typography
												variant="body2"
												color="textSecondary"
												sx={{mb: 2}}
												dangerouslySetInnerHTML={{__html: version.description}}
											/>

											<Box sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center'
											}}>
												<Typography variant="caption" color="textSecondary">
													Créée le {new Date(version.createdAt).toLocaleDateString('fr-FR')}
												</Typography>

												{version.userConsentCount !== undefined && (
													<Typography variant="caption" color="textSecondary">
														{version.userConsentCount} consentement{version.userConsentCount > 1 ? 's' : ''}
													</Typography>
												)}
											</Box>
										</CardContent>
									</Card>
								</Grid>
							))}
						</Grid>
					) : (
						<Box sx={{textAlign: 'center', py: 4}}>
							<Typography variant="body2" color="textSecondary">
								Aucune version disponible
							</Typography>
						</Box>
					)}
				</Tab>

				<Tab label="Statistiques">
					<Typography variant="h6" gutterBottom>
						Statistiques d'usage
					</Typography>

					<Grid container spacing={3} mb={2}>
						<Grid item xs={12} sm={6} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography color="textSecondary" gutterBottom>
										Consentements actifs
									</Typography>
									<Typography variant="h4">
										{record?.userConsentCount || 0}
									</Typography>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<Card variant="outlined">
								<CardContent>
									<Typography color="textSecondary" gutterBottom>
										Version
									</Typography>
									<Typography variant="h4">
										{record?.version}
									</Typography>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<Card variant="outlined" sx={{height: '100%'}}>
								<CardContent>
									<Typography color="textSecondary" gutterBottom>
										Type de version
									</Typography>
									{record &&
                                        <Chip
                                            label={record.versionType === 'MAJOR' ? 'Majeure' : 'Mineure'}
                                            color={record.versionType === 'MAJOR' ? 'primary' : 'default'}
                                        />
									}
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<Card variant="outlined" sx={{height: '100%'}}>
								<CardContent>
									<Typography color="textSecondary" gutterBottom>
										Statut
									</Typography>
									<Chip
										label={record?.isActive ? 'Actif' : 'Inactif'}
										color={record?.isActive ? 'success' : 'default'}
									/>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</Tab>

				<Tab label="Consentements">
					<ReferenceManyField
						reference="user-consents"
						target="consentPurpose.id"
						label="Consentements des utilisateurs"
						perPage={25}
						pagination={<Pagination/>}
					>
						<ConsentFilters/>
						<Datagrid
							bulkActionButtons={false}
							rowClick={(id, basePath) => `/user-consents/${id}/show`}
						>
							<FunctionField
								source="hashed_email"
								label="Hash"
								render={(record: UserConsent) => (
									<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
										<Typography variant="caption" sx={{fontFamily: 'monospace', fontSize: '11px'}}>
											{record.hashed_email ?
												record.hashed_email.substring(0, 12) + '...'
												: 'N/A'}
										</Typography>
										{record.hashed_email && (
											<IconButton
												size="small"
												onClick={(e) => {
													e.stopPropagation() // Empêche le clic sur la ligne
													handleCopyHash(record.hashed_email)
												}}
												title="Copier le hash complet"
												sx={{p: 0.25}}
											>
												<ContentCopyIcon sx={{fontSize: 12}}/>
											</IconButton>
										)}
									</Box>
								)}
							/>
							<TextField source="user.email" label="Email utilisateur"/>
							<FunctionField
								source="consentPurpose.version"
								label="Version"
								render={(record: UserConsent) => (
									<Stack direction="row" spacing={1} alignItems="center">
										<Typography variant="body2" fontWeight="medium">
											v{record.consentPurpose?.version || 'N/A'}
										</Typography>
										<Chip
											label={record.consentPurpose?.versionType === 'MAJOR' ? 'Majeure' : 'Mineure'}
											color={record.consentPurpose?.versionType === 'MAJOR' ? 'primary' : 'default'}
											size="small"
										/>
										{!record.consentPurpose?.isActive && (
											<Chip
												label="INACTIVE"
												color="warning"
												size="small"
												variant="outlined"
											/>
										)}
									</Stack>
								)}
							/>
							<FunctionField
								source="isGranted"
								label="Consentement"
								render={(record: UserConsent) => (
									<Chip
										label={record.isGranted ? 'Accordé' : 'Refusé'}
										color={record.isGranted ? 'success' : 'error'}
										size="small"
									/>
								)}
							/>
							<DateField source="consentedAt" label="Date" showTime/>
							<TextField source="source" label="Source"/>
							<FunctionField
								source="ipAddress"
								label="IP"
								render={(record: UserConsent) => (
									<Typography variant="caption" sx={{fontFamily: 'monospace'}}>
										{record.ipAddress}
									</Typography>
								)}
							/>
						</Datagrid>
					</ReferenceManyField>
				</Tab>
			</TabbedShowLayout>
		</Show>
	)
}
